import axios from 'axios';
import { BehaviorSubject, take, filter } from 'rxjs';


/**
 * @module Utils.ApiService
 */

/*
 * Makes all API calls to Tend servers.
 * Strictly no other code should make API calls to Tend servers except dev-only code.
 * Note that most calls are made to API servers, but some are made to Tend web servers.
 * Almost all functions return Promises that resolve to an HTTP Response object as documented here:
 * {@link https://github.com/axios/axios }
 * @param {Object} logService Application logger
 * @param {Object} util SDK utilities
 * @param {Object} _window Must point to a browser global execution context, not a worker or server
 * @returns {ApiService} An instance of an api service
 */
function createApiService(logService, util, environment, _window) {
    const _apiService = {};
    const axiosInstance = axios.create();
    const noLogApiConfigKeys = 'transformRequest,transformResponse,timeout,xsrfCookieName,'
        + 'xsrfHeaderName,maxContentLength,validateStatus,headers,method,baseURL,cache,url,'
        + 'params,data,adapter,responseType';
    const GET = 'get';
    const POST = 'post';
    const PUT = 'put';
    const DELETE = 'delete';
    const traceHeaderUpperCase = 'X-Correlation-ID';
    const traceHeaderLowerCase = 'x-correlation-id';
    const authHeader = 'Authorization';
    const guestCodeHeader = 'GuestCode';
    const sessionName = 'SeedonkSession';
    const BearerName = 'Bearer';
    const pathParamRegex = /\{(.*?)\}/g;

    // start off assuming the user is logged in
    const loggedInSubject = new BehaviorSubject(true);

    if (environment.auth === environment.authType.bearer
            || environment.auth === environment.authType.seedonkSession) {
        // modern auth method with Authorization headers - start off assuming the user is logged out
        loggedInSubject.next(false);
    }

    let guestCode;
    let askedForToken;
    let tokenExpired;

    function log(params) {
        params.module = 'apiService';
        logService.log(params);
        return params;
    }

    function badParams(params) {
        try {
            return new Error('bad params ' + JSON.stringify(params));
        } catch (e) {
            return e;
        }
    }

    function rejectWithBadParams(params) {
        return Promise.reject(badParams(params));
    }

    function logApiError(rejection, code) {
        const report = {};

        if (rejection?.config?.noLog) {
            return;
        }

        if (rejection?.config) {
            report.m = rejection?.config?.method;
            report.u = rejection?.config?.url?.replace('https://', '');
            report.q = rejection?.config?.params;
            report.s = rejection?.response?.status;

            // record any extra config properties that the app could be using
            Object.keys(rejection.config).forEach(key => {
                if (noLogApiConfigKeys.indexOf(key) === -1) {
                    report.config ??= {};
                    if (Array.isArray(rejection.config[key])) {
                        report.config[key] = 'array';
                    } else if (typeof rejection.config[key] === 'object') {
                        report.config[key] = 'object';
                    } else if (typeof rejection.config[key] === 'function') {
                        report.config[key] = 'function';
                    } else {
                        report.config[key] = String(rejection.config[key]).substring(0, 25);
                    }
                }
            });
        }

        if (rejection?.data) {
            if (Array.isArray(rejection.data.errors)) {
                report.errors = [];
                rejection.data.errors.forEach(e => report.errors.push(e.code));
            } else if (rejection.data.result) {
                report.error = rejection.data.result;
            } else if (rejection.data.reason) {
                report.error = rejection.data.reason;
            } else {
                if (rejection.data) {
                    if (Array.isArray(rejection.data) || typeof rejection.data === 'string') {
                        if (rejection.data.indexOf('<html>') === 0) {
                            report.error = '<html>';
                        } else {
                            report.error = JSON.stringify(rejection.data);
                        }
                    } else {
                        report.error = JSON.stringify(rejection.data);
                    }
                }
            }
        }

        if (rejection?.config) {
            // for security, only log data when the config was not marked with noLogData
            if (!rejection.config.noLogData) {
                report.d = rejection.config.data;
            }
            report.a = rejection.config.headers?.Accept;
            report.ct = rejection.config['Content-Type'];
        }

        log({
            code: code,
            int: report.s,
            object: report,
            error: rejection
        });
    }

    axiosInstance.defaults.headers.post.AntiCSRF = 'AntiCSRF';
    axiosInstance.defaults.headers.delete.AntiCSRF = 'AntiCSRF';
    axiosInstance.defaults.headers.put.AntiCSRF = 'AntiCSRF';
    axiosInstance.defaults.baseURL = environment.apiUrlBase;
    axiosInstance.defaults.cache = false;

    axiosInstance.interceptors.request.use(
        async config => {

            // if using Authorization header, and the user is not logged in,
            // and haven't asked for a token, then ask for a token
            if (!askedForToken && (environment.auth === environment.authType.bearer
                    || environment.auth === environment.authType.seedonkSession)
                    && !loggedInSubject.getValue()) {
                util.nativeMethod(
                    'requestAuthToken',
                    {
                        refresh: false,
                        traceId: util.randomBase64Code(15)
                    },
                    log
                );
                askedForToken = true;
            }

            return new Promise((resolve, reject) => {
                // only proceed with an API call if the user is logged in, which may happen asychronously,
                // or auth can be ignored
                loggedInSubject.pipe(filter(l => {
                    return l === true || config.ignoreAuthModule;
                }), take(1)).subscribe({
                    next: () => {
                        // paths that contain the term 'undefined' are an indication of an error,
                        // so track that
                        if (config?.url?.toLocaleLowerCase().includes('undefined')) {
                            log({
                                code: 'undefinedInUrl',
                                descr: 'URL contains "undefined"',
                                object: config,
                                traceId: config?.traceId,
                                deviceId: config?.deviceId
                            });
                        }

                        if (environment.oauthToken) {
                            // use auth header with bearer token if oauthToken is available
                            config.headers[authHeader] = BearerName + ' ' + environment.oauthToken;
                        } else if (environment.sessionId) {
                            // use auth header with SeedonkSession if sessionId is available
                            config.headers[authHeader] = sessionName + ' ' + environment.sessionId;
                        }

                        // use provided traceId or crate new traceId if none provided
                        config.headers[traceHeaderUpperCase] = config.traceId ?? util.randomBase64Code(15);
                        resolve(config);
                    },
                    error: e => {
                        logApiError(e, 'apiRequest');
                        reject(e);
                    }
                });
            });

        },
        error => {
            logApiError(error, 'apiRequest');
            return Promise.reject(error);
        }
    );

    axiosInstance.interceptors.response.use(
        response => {
            if (loggedInSubject.getValue()) {
                if (response.config.url === '/auth/logout') {
                    loggedInSubject.next(false);
                }
            } else {
                if (response.config.resetLoggedIn) {
                    loggedInSubject.next(true);
                }
            }

            response.traceId = response?.headers?.[traceHeaderLowerCase];

            return response;
        },
        error => {
            if (error?.response) {
                error.traceId = error.response?.headers[traceHeaderLowerCase];
                if (error.response.status === 401) {

                    if (!tokenExpired && (environment.auth === environment.authType.bearer
                            || environment.auth === environment.authType.seedonkSession)) {
                        // The requestAuthToken callback along with refresh==true tells
                        // the native app to relaod the page, since the web app cannot recover from this
                        tokenExpired = true;
                        util.nativeMethod(
                            'requestAuthToken',
                            {
                                refresh: true,
                                traceId: util.randomBase64Code(15)
                            },
                            log
                        );
                    }

                    if (!error.response.config.ignoreAuthModule) {
                        loggedInSubject.next(false);
                    }
                } else {
                    if (error.response.status !== -1
                            && error.response.status !== 400
                            && error.response.status !== 404) {
                        logApiError(error, 'apiResponse');
                    }
                }
            }
            return Promise.reject(error);
        }
    );

    /**
     * @param {Object} params
     * @param {String} params.type the entry in mediaDownloadInfo to use
     * @param {Object} [params.apiOptions]
     * @param {Object} [params.apiOptions.refresh]
     * @param {Object} [params.apiOptions.traceId]
     * @param {Object} params.pathInfo
     * @param {Object} params.queryInfo
     * @returns {Promise<String>} resolves to a URL from mediaDownloadInfo with values filled in
     */
    function getMediaInfoUrl(params) {
        return getMediaDownloadInfo(params.apiOptions).then(
            mediaInfo => {
                // The URL starts with the urlPattern from mediaInfo
                let urlString = mediaInfo?.data?.[params?.type]?.urlPattern ?? '';
                if (urlString.length < 4) {
                    return rejectWithBadParams(params);
                }

                // ensure that all the pathParams from the url can be filled
                const pathParamsInUrl = [... urlString.match(pathParamRegex)];
                const missingPathParams = pathParamsInUrl.filter(p => {
                    return params?.pathInfo[p.replace('{', '').replace('}', '')] === undefined;
                });
                if (missingPathParams.length > 0) {
                    return Promise.reject('getMediaInfoUrl ' + params?.type + ' is missing pathParams '
                        + JSON.stringify(missingPathParams));
                }

                // Apply all the path params
                Object.entries(params?.pathInfo).forEach(([key, value]) => {
                    urlString = urlString.replace('{' + key + '}', encodeURIComponent(value));
                });

                if (urlString.startsWith('/')) {
                    // prepend missing origin or an error will result
                    urlString = _window.location.origin + urlString;
                }

                // add query params
                const url = new URL(urlString);
                Object.entries(params?.queryInfo).forEach(([key, value]) => {
                    if (value) {
                        url.searchParams.append(key, value);
                    }
                });

                return url.toString();
            },
            e => Promise.reject(e)
        );
    }

    /**
     * @param {Object} params
     * @param {String} params.type the entry in mediaDownloadInfo to use
     * @param {Object} [params.apiOptions]
     * @param {Object} [params.apiOptions.refresh]
     * @param {Object} [params.apiOptions.traceId]
     * @param {Object} params.pathInfo
     * @param {Object} params.queryInfo
     * @returns {Promise<Object>} resolves to an API response object
     */
    function getMediaInfoData(params) {
        return getMediaInfoUrl(params).then(
            url => {
                return axiosInstance({
                    method: GET,
                    url: url,
                    baseURL: '',
                    traceId: params?.traceId
                });
            },
            e => Promise.reject(e)
        );
    }

    _apiService.loggedIn = function() {
        return loggedInSubject.asObservable();
    };

    _apiService.resetAuth = function() {
        loggedInSubject.next(true);
    };

    /**
     * Sets the GuestCode header for all requests
     */
    _apiService.setGuestCode = function(guestCodeParam) {
        if (guestCodeParam) {
            guestCode = guestCodeParam;
            axiosInstance.defaults.headers.common[guestCodeHeader] = guestCode;
        } else {
            delete axiosInstance.defaults.headers.common[guestCodeHeader];
            guestCode = undefined;
        }
    };

    /**
     * @param {Object} params Object containing info about the user context
     * @param {String} params.locale The locale for the user's language and region, e.g. "en-US"
     * @param {String} [params.traceId]
     * @returns {Promise} a Promise that resolves to a response object where response.data is
     *     the localization data
     */
    _apiService.getLocalizations = function(params) {
        if (!params?.locale) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            baseURL: '',
            url: '/app/supportedLocalization',
            params: {
                locale: params.locale
            },
            traceId: params?.traceId
        });
    };

    /**
     * @returns {Promise} a list of supported localizations for the current partnerId
     */
    _apiService.getLocales = function(params) {
        return axiosInstance({
            method: GET,
            baseURL: environment.webUrlBase,
            url: '/app/supportedLocales',
            traceId: params?.traceId
        });
    };

    /**
     * @returns Info about what environment the app is running under.
     * Cache environment info (which is constant) to avoid multiple API calls.
     */
    let environmentPromise;
    _apiService.environment = function(params) {
        if (!environmentPromise) {
            environmentPromise = axiosInstance({
                method: GET,
                baseURL: environment.webUrlBase,
                url: '/environment',
                traceId: params?.traceId
            }).then(
                result => {
                    return result.data;
                },
                e => Promise.reject(e)
            );
        }
        return environmentPromise;
    };

    /**
     * @returns {Promise} A Promise that resolves to a list of wold timezones as used by all cameras
     */
    _apiService.timezones = function(params) {
        return axiosInstance({
            method: GET,
            headers: {'Accept': 'application/json,*/*'},
            baseURL: environment.webUrlBase,
            url: '/timezones',
            traceId: params?.traceId
        });
    };

    /**
     * @returns {Promise} A Promise that resovles to info about a given camera product,
     *     including a description suitable for alt tags and a URL to a product image for UI use
     */
    _apiService.productImageData = function(params) {
        if (!params?.model) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            baseURL: environment.webUrlBase,
            url: '/productImageData/' + encodeURIComponent(params.model),
            traceId: params.traceId
        });
    };

    /**
     * @description starts media streaming after calling "/init" on a WebSocket
     * @param {Object} params
     * @param {String} params.deviceId
     * @param {Boolean} params.p2p should be false
     * @param {String} params.type should be "VIDEO" or "AUDIO"
     * @param {String} params.sessionId the current viewer session
     * @param {Number} params.keepaliveSec should be 10
     * @param {String} [params.traceId]
     * @returns {Promise} Resolves on successful API call
     */
    _apiService.startMedia = function(params) {
        if (!params?.deviceId || params?.p2p === undefined || !params?.type
                || !params?.sessionId || !params?.keepaliveSec) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/video/startPluginMedia/' + encodeURIComponent(params.deviceId),
            data: {
                p2p: params.p2p,
                type: params.type,
                sessionId: params.sessionId,
                keepaliveSec: params.keepaliveSec
            },
            traceId: params.traceId
        });
    };

    // ==============================================================================
    _apiService.oauthClient = {};

    /**
     * @param {Object} params
     * @param {String} params.code
     * @param {String} params.providerId
     * @param {String} params.redirectUri
     * @param {String} [params.traceId]
     * @returns {Promise}
     */
    _apiService.oauthClient.getAccessToken = function(params) {
        if (!params || !params.providerId || !params.redirectUri || !params.code) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/oauth/do/processAuthCode',
            data: {
                code: params.code,
                providerId: params.providerId,
                redirectUri: params.redirectUri
            },
            traceId: params.traceId
        });
    };

    _apiService.oauthClient.getSocialMediaAccessTokenList = function(params) {
        return axiosInstance({
            method: GET,
            url: '/oauth/partners/providerAccessTokens',
            traceId: params?.traceId
        });
    };

    _apiService.oauthClient.getSocialMediaAccessToken = function(params) {
        if (!params || !params.providerId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            url: '/oauth/partners/providerAccessTokens/' + encodeURIComponent(params.providerId),
            traceId: params.traceId
        });
    };

    _apiService.oauthClient.deleteOAuthAccessToken = function(params) {
        if (!params || !params.providerId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: DELETE,
            url: '/oauth/partners/providerAccessTokens/' + encodeURIComponent(params.providerId),
            traceId: params.traceId
        });
    };

    _apiService.oauthClient.getOauthProvider = function(params) {
        if (!params || !params.providerId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            url: '/oauth/partners/providers/' + encodeURIComponent(params.providerId),
            traceId: params.traceId
        });
    };

    // ==============================================================================
    _apiService.social = {};

    /**
     * @param {Object} params
     * @param {String} params.providerId
     * @param {String} params.eventId
     * @param {String} params.title
     * @param {String} params.text
     * @param {String} [params.traceId]
     */
    _apiService.social.uploadEvent2SocialMedia = function(params) {
        if (!params) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/social/do/uploadEvent',
            data: {
                providerId: params.providerId,
                eventId: params.eventId,
                title: params.title,
                text: params.text
            },
            traceId: params.traceId
        });
    };

    // ==============================================================================
    _apiService.firmware = {};

    /**
     * Returns a 401 result for GuestCode
     */
    _apiService.firmware.getInformation = function(params) {
        if (!params?.id) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            url: '/devices/' + encodeURIComponent(params.id) + '/hw/firmwareInfo',
            traceId: params.traceId
        });
    };

    _apiService.firmware.startUpdate = function(params) {
        if (!params?.deviceId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/firmware/startUpgrade/' + encodeURIComponent(params.deviceId),
            traceId: params.traceId
        });
    };

    // ==============================================================================

    _apiService.services = {};

    _apiService.services.getPaymentProcessor = function(params) {
        if (!params?.paymentProcessorId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            url: '/services/paymentProcessors/' + encodeURIComponent(params.paymentProcessorId),
            traceId: params.traceId
        });
    };

    /**
     * Returns 401 response for GuestCode
     */
    _apiService.services.getProductsForDevice = function(params) {
        if (!params?.deviceId || !params?.paymentProcessorId || !params?.billingType) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            url: '/services/forDevice/' + encodeURIComponent(params.deviceId) + '/products',
            params: {
                paymentProcessorId: params.paymentProcessorId,
                billingType: params.billingType
            },
            deviceId: params.deviceId,
            billingType: params.billingType,
            traceId: params.traceId
        });
    };

    /**
     * Returns 401 response for GuestCode
     */
    _apiService.services.getFullStatusForDevice = function(params) {
        if (!params?.deviceId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            url: '/services/forDevice/' + encodeURIComponent(params.deviceId) + '/statusFull',
            traceId: params.traceId
        });
    };

    _apiService.services.getSubCurrent = function(params) {
        if (!params?.paymentProcessorId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            url: '/services/subscription/change',
            params: { paymentProcessor: params.paymentProcessorId },
            traceId: params.traceId
        });
    };

    _apiService.services.selectProductForSub = function(params) {
        if (!params?.deviceId || !params?.productId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/services/subscription/change/'
                + encodeURIComponent(params.deviceId) + '/do/selectProduct/'
                + encodeURIComponent(params.productId),
            traceId: params.traceId
        });
    };

    _apiService.services.unselectTypeForSub = function(params) {
        if (!params?.deviceId || !params?.productType) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/services/subscription/change/'
                + encodeURIComponent(params.deviceId) + '/do/unSelectAllProductsForProductType/'
                + encodeURIComponent(params.productType),
            data: params.surveyData,
            traceId: params.traceId
        });
    };

    // ==============================================================================
    _apiService.user = {};

    /**
     * Returns a 401 result for GuestCode
     */
    _apiService.user.getSelf = function(params) {
        return axiosInstance({
            method: GET,
            url: '/users/self',
            params: guestCode ? ({ GuestCode: guestCode }) : undefined,
            ignoreAuthModule: params?.ignoreAuthModule,
            traceId: params?.traceId
        });
    };

    _apiService.user.putSelf = function(params) {
        if (!params?.toSave) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: PUT,
            url: '/users/self',
            data: params.toSave,
            traceId: params.traceId
        });
    };

    _apiService.user.emailVerification = function(params) {
        if (!params?.userId || !params?.token) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/users/self/do/verifyEmail',
            data: {
                userId: params.userId,
                token: params.token
            },
            ignoreAuthModule: true,
            traceId: params.traceId
        });
    };

    _apiService.user.resendEmailVerification = function(params) {
        return axiosInstance({
            method: POST,
            url: '/users/self/do/resendEmailVerification',
            ignoreAuthModule: true,
            traceId: params?.traceId
        });
    };

    // ==============================================================================
    _apiService.accessControl = {};

    /**
     * Works for GuestCode.
     * In results, fields 'name' and 'descr' are dependent on the current locale.
     */
    _apiService.accessControl.roles = function(params) {
        return axiosInstance({
            method: GET,
            url: '/accessControl/roles',
            params: { includeInactive: 'true' },
            ignoreAuthModule: true,
            traceId: params?.traceId
        });
    };

    // ==============================================================================
    _apiService.friends = {};

    _apiService.friends.get = function(params) {
        return axiosInstance({
            method: GET,
            url: '/friends',
            params: { version: '1'},
            traceId: params?.traceId
        });
    };

    _apiService.friends.acceptInvite = function(params) {
        if (!params?.token) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/friends/do/acceptInvitation',
            data: {
                token: params.token
            },
            traceId: params?.traceId
        });
    };

    _apiService.friends.remove = function(params) {
        if (!params?.friendId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: DELETE,
            url: '/friends/' + encodeURIComponent(params.friendId),
            traceId: params?.traceId
        });
    };

    // ==============================================================================
    _apiService.auth = {};

    /**
     * @param {Object} params
     * @param {String} params.oldPassword
     * @param {String} params.newPassword
     * @returns {Promise}
     */
    _apiService.auth.changePassword = function(params) {
        if (!params?.oldPassword || !params?.newPassword) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/auth/changePassword',
            data: {
                oldPassword: params.oldPassword,
                newPassword: params.newPassword
            },
            noLogData: true,
            traceId: params?.traceId
        });
    };

    /**
     * @param {Object} resetPasswordData
     * @param {String} resetPasswordData.username
     * @param {String} resetPasswordData.password
     * @param {String} resetPasswordData.token
     * @param {String} resetPasswordData.traceId
     * @returns {Promise}
     */
    _apiService.auth.requestResetPassword = function(params) {
        if (!params?.username || !params?.password || !params?.token) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/auth/forgotPassword/do/resetByToken',
            data: {
                username: params.username,
                password: params.password,
                token: params.token
            },
            ignoreAuthModule: true,
            noLogData: true,
            traceId: params.traceId
        });
    };

    _apiService.auth.verifyResetPassword = function(params) {
        if (!params?.token) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            url: '/auth/forgotPassword/tokens/' + encodeURIComponent(params.token),
            ignoreAuthModule: true,
            traceId: params.traceId
        });
    };

    _apiService.auth.loadOauthProviderList = function(params) {
        return axiosInstance({
            method: GET,
            url: '/oauth/partners/providers',
            traceId: params?.traceId
        });
    };

    _apiService.auth.loadOauthConsumer = function(params) {
        return axiosInstance({
            method: GET,
            url: '/oauthServer/partners/consumers/' + encodeURIComponent(params.oauthClientId),
            traceId: params?.traceId
        });
    };

    _apiService.auth.getOauthConsumers = function(params) {
        return axiosInstance({
            method: GET,
            url: '/oauthServer/partners/consumers',
            traceId: params?.traceId
        });
    };

    _apiService.auth.getConsumerAccessTokens = function(params) {
        return axiosInstance({
            method: GET,
            url: '/oauthServer/consumerAccessTokens',
            traceId: params?.traceId
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.response_type
     * @param {String} params.client_id
     * @param {String} params.redirect_uri
     * @param {String} params.scope
     * @param {String} params.state
     * @param {String} [params.traceId]
     * @returns {Promise}
     */
    _apiService.auth.grantCode = function(params) {
        if (!params?.response_type || !params?.client_id
                || !params?.redirect_uri || !params?.scope || !params?.state) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            url: '/oauthServer/codeGrant',
            params: {
                response_type: params.response_type,
                client_id: params.client_id,
                redirect_uri: params.redirect_uri,
                scope: params.scope,
                state: params.state
            },
            noLogData: true,
            oauthData: params,
            traceId: params.traceId
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.response_type
     * @param {String} params.client_id
     * @param {String} params.redirect_uri
     * @param {String} params.scope
     * @param {String} params.state
     * @param {String} [params.traceId]
     * @returns {Promise}
     */
    _apiService.auth.grantImplicit = function(params) {
        if (!params?.response_type || !params?.client_id
                || !params?.redirect_uri || !params?.scope || !params?.state) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            url: '/oauthServer/implicitGrant',
            params: {
                response_type: params.response_type,
                client_id: params.client_id,
                redirect_uri: params.redirect_uri,
                scope: params.scope,
                state: params.state
            },
            noLogData: true,
            traceId: params.traceId
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.code
     * @param {String} params.providerId
     * @param {String} params.redirectUri
     */
    // Not used as of December 2022
    _apiService.auth.accessToken = function(params) {
        if (!params?.code || !params?.providerId || !params?.redirectUri) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            url: '/oauth/receiveToken',
            params: {
                code: params.code,
                provider_id: params.providerId,
                redirect_uri: params.redirectUri
            },
            traceId: params.traceId
        });
    };

    _apiService.auth.login = function(params) {
        if (!params?.username || !params?.password) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/auth/login',
            data: {
                src: 'web',
                platform: 'web',
                newApi: true,
                username: params.username,
                password: params.password
            },
            ignoreAuthModule: true,
            noLogData: true,
            resetLoggedIn: true,
            traceId: params.traceId
        });
    };

    _apiService.auth.logout = function(params) {
        return axiosInstance({
            method: POST,
            url: '/auth/logout',
            resetLoggedIn: true,
            ignoreAuthModule: true,
            traceId: params?.traceId
        });
    };

    _apiService.auth.signup = function(params) {
        if (!params?.username || !params?.password || !params?.email) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/auth/signup',
            data: {
                newApi: true,
                username: params.username,
                password: params.password,
                email: params.email
            },
            ignoreAuthModule: true,
            noLogData: true,
            resetLoggedIn: true,
            traceId: params.traceId
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.email
     * @param {String} [params.traceId]
     * @returns {Promise}
     */
    _apiService.auth.forgotUsername = function(params) {
        if (!params?.email) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/auth/forgotUsername/do/initiate',
            data: {
                email: params.email
            },
            ignoreAuthModule: true,
            noLogData: true,
            traceId: params.traceId
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.username
     * @param {String} params.email
     * @param {String} [params.traceId]
     * @returns {Promise} Could resolve to a redirect, success or failure
     */
    _apiService.auth.forgotPassword = function(params) {
        if (!params?.username || !params?.email) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/auth/forgotPassword/do/initiate',
            data: {
                username: params.username,
                email: params.email
            },
            noLogData: true,
            ignoreAuthModule: true,
            traceId: params.traceId
        });
    };

    _apiService.auth.loginSid = function(params) {
        if (!params?.username || !params?.password || !params?.sid) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/auth/loginSid',
            data: {
                username: params.username,
                password: params.password,
                sid: params.sid
            },
            ignoreAuthModule: true,
            noLogData: true,
            resetLoggedIn: true,
            traceId: params.traceId
        });
    };

    // ==============================================================================
    _apiService.events = {};

    /**
     * See {@link https://seedonk.atlassian.net/wiki/display/CORE/Events+API}
     * See {@link https://server.dev.tendplatform.com/api-doc/raml/index.html#/events}
     * @param {Object} params
     * @param {String} params.srcId
     * @param {String} params.scrType
     * @param {String} params.deviceAlias
     * @param {String} params.startDttm
     * @param {String} params.endDttm
     * @param {Number} params.limit
     * @param {String} params.sinceEvent
     * @param {String} params.beforeEvent
     * @param {String} params.v - version
     * @param {String} params.action - not sent to server but for internal SDK use
     * @param {Date} params.date - not sent to server but for internal SDK use
     * @param {String} [params.traceId] - sent as HTTP header by request interceptor
     */
    _apiService.events.list = function(params) {
        if (!params || typeof params !== 'object') {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            url: '/events',
            params: {
                srcId: params.srcId,
                scrType: params.scrType,
                deviceAlias: params.deviceAlias,
                startDttm: params.startDttm,
                endDttm: params.endDttm,
                limit: params.limit,
                sinceEvent: params.sinceEvent,
                beforeEvent: params.beforeEvent,
                v: params.v
            },
            action: params.action,
            date: params.date,
            traceId: params.traceId
        });
    };

    _apiService.events.getEvent = function(params) {
        if (!params?.id) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            url: '/events/' + encodeURIComponent(params.id),
            action: params.action,
            params: {
                shareCode: params.shareCode,
                v: params.v
            },
            traceId: params.traceId
        });
    };

    /**
     * Only need to make an API call once per page load,
     * so save the Promise returned from the first call and
     * provide it for future calls.
     * The result contains URL patterns for various media related
     * events, such as for download, playback, thumbnail images, and more.
     * This allows URL patterns to be dynanic and not hardcoded.
     */
    let mediaDownloadPromise;
    function getMediaDownloadInfo(params) {
        if (mediaDownloadPromise === undefined || params?.refresh) {
            mediaDownloadPromise = axiosInstance({
                method: GET,
                url: '/events/mediaDownloadInfo',
                traceId: params?.traceId
            });
        }
        return mediaDownloadPromise;
    }

    /**
     * Tracking data for objects detected within an event video.
     * The info can be overlaid on top of video.
     */
    _apiService.events.getObjectTrack = function(params) {
        return getMediaInfoData({
            type: 'objectTrack',
            apiOptions: params,
            pathInfo: { eventId: params?.eventId},
            queryInfo: { shareCode: params.shareCode }
        });
    };

    /**
     * Deprecated. Time intervals for still frames taken from an event.
     */
    _apiService.events.getFrameTimes = function(params) {
        return getMediaInfoData({
            type: 'frameTimes',
            apiOptions: params,
            pathInfo: { eventId: params?.eventId},
            queryInfo: { shareCode: params.shareCode }
        });
    };

    // TODO: call from eventService
    // Not used as of December 2022
    _apiService.events.getComments = function(params) {
        if (!params?.eventId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            url: '/events/' + encodeURIComponent(params.eventId) + '/comments',
            params: { shareCode: params.shareCode },
            traceId: params.traceId
        });
    };

    // TODO: call from eventService
    // Not used as of December 2022
    _apiService.events.postComment = function(params) {
        if (!params?.eventId || !params?.text) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/events/' + encodeURIComponent(params.eventId) + '/comments',
            data: {
                'text': params.text
            },
            params: { shareCode: params.shareCode },
            traceId: params.traceId
        });
    };

    // returns an ArrayBuffer of video data
    _apiService.events.getVideoArrayBuffer = function(params) {
        if (!params?.event?.id || !(typeof params.num === 'number') || !params?.length) {
            return rejectWithBadParams(params);
        }
        return getMediaInfoUrl({
            type: 'videoCdn',
            apiOptions: params,
            pathInfo: { eventId: params?.event.id},
            queryInfo: { SeedonkSession: environment.sessionId }
        }).then(
            url => {
                return axiosInstance({
                    url: url,
                    method: GET,
                    headers: {
                        'Content-Type': 'video/mp4',
                        'Accept': 'video/mp4, */*'
                    },
                    event: params.event,
                    responseType: 'arraybuffer',
                    params: { num: params.num },
                    baseURL: '',
                    length: params.length,
                    num: params.num,
                    traceId: params.traceId
                });
            },
            e => Promise.reject(e)
        );
    };

    // returns a Promise that resolves to a video URL on the CDN
    _apiService.events.getVideoUri = function(params) {
        if (!params?.event?.id) {
            return rejectWithBadParams(params);
        }
        return getMediaInfoUrl({
            type: 'videoCdn',
            apiOptions: params,
            pathInfo: { eventId: params?.event?.id },
            queryInfo: { SeedonkSession: environment.sessionId}
        });
    };

    // returns a Promise that resolves to a thumbnail image URL
    _apiService.events.getThumbUri = function(params) {
        if (!params?.event?.id) {
            return rejectWithBadParams(params);
        }
        return getMediaInfoUrl({
            type: 'thumbnail',
            apiOptions: params,
            pathInfo: { eventId: params?.event?.id},
            queryInfo: { shareCode: params.shareCode, SeedonkSession: environment.sessionId }
        });
    };

    // returns a Promise that resolves to a face crop image URL
    _apiService.events.getFaceCropUri = function(params) {
        return getMediaInfoUrl({
            type: 'faceCrops',
            apiOptions: params,
            pathInfo: { eventId: params?.eventId, num: params.cropId},
            queryInfo: { shareCode: params.shareCode, SeedonkSession: environment.sessionId }
        });
    };

    _apiService.events.getFaceImage = function(params) {
        if (!params?.identityId || !params?.imageId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            headers: {
                'Accept': 'image/jpeg, */*'
            },
            responseType: 'arraybuffer',
            url: '/faces/identities/'
                + encodeURIComponent(params?.identityId)
                + '/images/'
                + encodeURIComponent(params?.imageId),
            traceId: params.traceId
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.identityId
     * @param {String} params.traceId
     * @returns {ArrayBuffer}
     */
    _apiService.events.getFacePrimaryImage = function(params) {
        if (!params?.identityId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            headers: {
                'Accept': 'image/jpeg, */*'
            },
            responseType: 'arraybuffer',
            url: '/faces/identities/'
                + encodeURIComponent(params?.identityId)
                + '/images/primaryImage',
            traceId: params.traceId
        });
    };

    // returns a Promise that resolves to an array of all image frame URLs
    _apiService.events.getImageFrameUris = function(params) {
        if (!params?.eventId) {
            return rejectWithBadParams(params);
        }
        const arr = [];
        let i = 0;
        while (i < (params.length || 0)) {
            arr.push(getMediaInfoUrl({
                type: 'imageFrames',
                apiOptions: params,
                pathInfo: { eventId: params?.eventId, num: i },
                queryInfo: { shareCode: params.shareCode, SeedonkSession: environment.sessionId }
            }));
            i += 1;
        }
        return Promise.all(arr);
    };

    _apiService.events.getDownloadUri = function(params) {
        if (!params?.event?.id) {
            return rejectWithBadParams(params);
        }
        return getMediaInfoUrl({
            type: 'download',
            apiOptions: params,
            pathInfo: { eventId: params?.event?.id },
            queryInfo: { shareCode: params?.shareCode, SeedonkSession: environment.sessionId }
        });
    };

    _apiService.events.getFsmData = function(params) {
        return getMediaInfoData({
            type: 'va-GarageFSM1.json',
            apiOptions: params,
            pathInfo: { eventId: params?.eventId },
            queryInfo: { shareCode: params?.shareCode }
        });
    };

    _apiService.events.getFsmImageUri = function(params) {
        return getMediaInfoUrl({
            type: 'fsmSnap.jpg',
            apiOptions: params,
            pathInfo: { eventId: params?.eventId, num: params?.frameNum },
            queryInfo: { SeedonkSession: environment.sessionId}
        });
    };

    _apiService.events.postAnalyticsFeedback = function(params) {
        if (!params?.data) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/analytics/v2/examples',
            data: params.data,
            traceId: params.traceId
        });
    };

    _apiService.events.put = function(params) {
        if (!params?.eventId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: PUT,
            url: '/events/' + encodeURIComponent(params.eventId),
            data: params.data,
            traceId: params.traceId
        });
    };

    // As of March 30, 2022 and December 2022, this is not used
    _apiService.events.create = function(params) {
        if (!params?.data) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/events',
            data: params.data,
            traceId: params.traceId
        });
    };

    _apiService.events.removeById = function(params) {
        if (!params?.eventId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: DELETE,
            url: '/events/' + encodeURIComponent(params.eventId),
            eventId: params.eventId,
            traceId: params.traceId
        });
    };

    _apiService.events.remove = function(params) {
        const cleanParams = util.merge({}, params);
        cleanParams.traceId = undefined;

        // cleanParams must be an object with required properties
        if (typeof cleanParams === 'object') {
            if (cleanParams.eventIdList) {
                if (Array.isArray(cleanParams.eventIdList)) {
                    // if there is eventIdList, ensure it is the only param
                    Object.keys(cleanParams).forEach(key => {
                        if (key !== 'eventIdList') {
                            cleanParams[key] = undefined;
                        }
                    });
                } else {
                    return rejectWithBadParams(params);
                }
            } else if (typeof cleanParams.startDttm === 'string'
                    && typeof cleanParams.endDttm === 'string'
                    && typeof cleanParams.srcType === 'string'
                    && Array.isArray(cleanParams.srcIdList)) {
                cleanParams.softDelete = true; // never allow permanent removal
                cleanParams.favorites = false; // never allow favorites to be removed
            } else {
                return rejectWithBadParams(params);
            }
        } else {
            return rejectWithBadParams(params);
        }

        return axiosInstance({
            method: POST,
            data: cleanParams,
            removeParams: cleanParams,
            url: '/events/do/deleteMultiple',
            traceId: params.traceId
        });
    };

    _apiService.events.deviceIdsWithEvents = function(params) {
        return axiosInstance({
            method: GET,
            url: '/events/deviceIdsWithEvents',
            traceId: params?.traceId
        });
    };

    /**
     * @param {Object} [params]
     * @param {String} [params.tz] time zone
     * @param {String} [params.traceId]
     */
    _apiService.events.count = function(params) {
        let tz;
        if (params && params.tz) {
            tz = params.tz;
        } else {
            const offset = new _window.Date().getTimezoneOffset();
            const tzHours = Math.floor(offset / 60);
            const tzMinutes = Math.floor(offset - tzHours * 60);
            tz = tzHours < 0 ? '+' : '-';
            tz += Math.abs(tzHours).toString().padStart(2, '0');
            tz += ':';
            tz += tzMinutes.toString().padStart(2, '0');
        }
        return axiosInstance({
            method: GET,
            url: '/events/count',
            params: {
                tz: tz
            },
            traceId: params?.traceId
        });
    };

    _apiService.events.postFeedback = function(params) {
        if (!params?.data || !params?.eventId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            data: params.data,
            url: '/events/' + encodeURIComponent(params.eventId) + '/feedback',
            traceId: params.traceId
        });
    };

    _apiService.events.putFeedback = function(params) {
        if (!params?.data || !params?.eventId || !params?.feedbackId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: PUT,
            data: params.data,
            url: '/events/' + encodeURIComponent(params.eventId) + '/feedback/'
                + encodeURIComponent(params.feedbackId),
            traceId: params.traceId
        });
    };

    _apiService.events.eventApiUrl = function(event) {
        const eventId = event?.id ?? event;
        if (typeof eventId === 'string') {
            return environment.apiUrlBase + '/events/' + eventId;
        }
    };

    // ==============================================================================
    _apiService.devices = {};

    /**
     * Works for GuestCode
     */
    _apiService.devices.get = function(params) {
        if (params && params.id) {
            return axiosInstance({
                method: GET,
                url: '/devices/' + encodeURIComponent(params.id),
                params: {
                    lightweight: params.lightweight,
                    GuestCode: params.guestCode
                },
                traceId: params.traceId
            });
        }

        return axiosInstance({
            method: GET,
            url: '/devices',
            params: {
                includeDeleted: true,
                lightweight: params?.lightweight,
                GuestCode: params?.guestCode
            },
            traceId: params?.traceId
        });
    };

    _apiService.devices.save = function(params) {
        if (!params?.device?.id || !params?.device?.settings) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: PUT,
            url: '/devices/' + encodeURIComponent(params.device.id) + '/settings',
            data: params.device.settings,
            traceId: params.traceId
        });
    };

    _apiService.devices.resetViewState = function(params) {
        if (!params?.deviceId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/devices/' + encodeURIComponent(params.deviceId) + '/cam/do/resetPtz',
            traceId: params.traceId
        });
    };

    _apiService.devices.setPtz = function(params) {
        if (!params?.deviceId || (!params.p && !params.t && !params.z)) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/devices/' + encodeURIComponent(params.deviceId) + '/cam/do/setPtz',
            data: {
                p: params.p,
                t: params.t,
                z: params.z
            },
            traceId: params.traceId
        });
    };

    _apiService.devices.remove = function(params) {
        if (!params?.device?.id) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: DELETE,
            url: '/devices/' + encodeURIComponent(params.device.id),
            traceId: params.traceId
        });
    };

    _apiService.devices.createMediaSession = function(params) {
        if (!params?.deviceId || !params?.keepaliveSec) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/devices/' + encodeURIComponent(params.deviceId) + '/cam/do/createViewerSession',
            data: {
                keepaliveSec: params.keepaliveSec
            },
            traceId: params.traceId
        });
    };

    /**
     * Result is 401 for GuestCode
     */
    _apiService.devices.accessLevels = function(params) {
        return axiosInstance({
            method: GET,
            url: '/devices/myAccessLevels',
            params: { includeDeleted: true },
            traceId: params?.traceId
        });
    };

    _apiService.devices.getDirectAccessGrants = function(params) {
        return axiosInstance({
            method: GET,
            url: '/devices/directAccessGrantsForAllDevices',
            traceId: params?.traceId
        });
    };

    _apiService.devices.setGrant = function(params) {
        if (!params?.grant?.deviceId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/devices/' + encodeURIComponent(params.grant.deviceId) + '/directAccessGrants',
            data: params.grant,
            traceId: params.traceId
        });
    };

    _apiService.devices.removeGrant = function(params) {
        if (!params?.grant?.deviceId || !params?.grant?.id) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: DELETE,
            url: '/devices/' + encodeURIComponent(params.grant.deviceId)
                + '/directAccessGrants/' + encodeURIComponent(params.grant.id),
            grant: params.grant,
            traceId: params.traceId
        });
    };

    _apiService.devices.sendUserInvitationForDevice = function(params) {
        if (!params?.deviceId || !params?.emailAddress) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/devices/' + encodeURIComponent(params.deviceId) + '/do/sendUserInvitation',
            data: {
                deviceId: params.deviceId,
                emailAddress: params.emailAddress,
                note: params.note,
                roleIdList: params.roleIdList
            },
            traceId: params.traceId
        });
    };

    _apiService.devices.getInvites = function(params) {
        return axiosInstance({
            method: GET,
            url: '/devices/userInvitationsForAllDevices',
            params: { version: '1' },
            traceId: params?.traceId
        });
    };

    _apiService.devices.removeInvite = function(params) {
        if (!params?.deviceId || !params?.inviteId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: DELETE,
            url: '/devices/' + encodeURIComponent(params.deviceId)
                + '/userInvitations/' + encodeURIComponent(params.inviteId),
            traceId: params.traceId
        });
    };

    _apiService.devices.guestInvite = function(params) {
        if (!params?.deviceId || !params?.emailAddress) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/devices/' + encodeURIComponent(params.deviceId) + '/do/sendGuestInvitation',
            data: {
                emailAddress: params.emailAddress,
                roleIdList: params.roleIdList,
                deviceId: params.deviceId
            },
            traceId: params.traceId
        });
    };

    /**
     * @returns {String} A URL for an m3u8 playlist suitable for HLS streaming,
     * optioanlly containing a guest code if one was previously set.
     */
    _apiService.devices.m3u8 = function(params) {
        if (!params?.deviceId) {
            throw badParams(params);
        }
        const url = new URL(
            environment.apiUrlBase
            + '/devices/'
            + encodeURIComponent(params.deviceId)
            + '/cam/liveStreamFmp4.m3u8'
        );
        if (params.guestCode ?? axiosInstance.defaults.headers.common[guestCodeHeader]) {
            // The name of this param must be title case
            url.searchParams.append(
                'GuestCode',
                params.guestCode ?? axiosInstance.defaults.headers.common[guestCodeHeader]
            );
        }
        if (params.sessionId ?? environment.sessionId) {
            url.searchParams.append('SeedonkSession', params.sessionId ?? environment.sessionId);
        }
        if (params.viewerSessionId) {
            url.searchParams.append('viewerSessionId', params.viewerSessionId);
        }
        return url.toString();
    };

    _apiService.devices.getRecentImage = function(params) {
        if (!params?.deviceId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            headers: {
                'Accept': 'image/jpeg, */*'
            },
            responseType: 'arraybuffer',
            url: '/devices/'
                + encodeURIComponent(params.deviceId)
                + '/cam/recentImage.jpg',
            params: {
                forceRefreshForTesting: Boolean(params.refresh),
                size: params.size
            },
            traceId: params.traceId
        });
    };

    // ==============================================================================
    _apiService.keepalive = function(params) {
        if (!params?.viewerSession) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/keepalive/' + encodeURIComponent(params.viewerSession),
            noLog: true,
            traceId: params.traceId
        });
    };

    // ==============================================================================
    _apiService.uiSettings = {};

    /**
     * Returns 401 response for GuestCode
     */
    _apiService.uiSettings.getForUser = function(params) {
        return axiosInstance({
            method: GET,
            url: '/uiSettings/user',
            traceId: params?.traceId
        });
    };

    _apiService.uiSettings.postForUser = function(params) {
        if (!params?.toSave) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/uiSettings/user',
            data: params.toSave,
            traceId: params.traceId
        });
    };

    /**
     * Returns 401 response for GuestCode
     */
    _apiService.uiSettings.getForDevices = function(params) {
        return axiosInstance({
            method: GET,
            url: '/uiSettings/devices',
            traceId: params?.traceId
        });
    };

    _apiService.uiSettings.postForDevice = function(params) {
        if (!params?.deviceId || !params?.toSave) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/uiSettings/devices/' + encodeURIComponent(params.deviceId),
            data: params.toSave,
            traceId: params.traceId
        });
    };

    // Not used as of December 2022
    _apiService.uiSettings.removeDevice = function(params) {
        if (!params?.deviceId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: DELETE,
            url: '/uiSettings/devices/' + encodeURIComponent(params.deviceId),
            traceId: params.traceId
        });
    };

    // ==============================================================================
    _apiService.faces = {};

    _apiService.faces.getIdentities = function(params) {
        return axiosInstance({
            method: GET,
            url: '/faces/identities',
            traceId: params?.traceId
        });
    };

    // Not used as of December 2022
    _apiService.faces.getIdentityImageInfo = function(params) {
        if (!params?.identityId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            url: '/faces/identities/' + encodeURIComponent(params.identityId) + '/imagesInfo',
            traceId: params?.traceId
        });
    };

    // Not used as of December 2022, but probably should be
    _apiService.faces.setIdentityLabel = function(params) {
        if (!params?.identityId || params?.label) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: PUT,
            url: '/faces/identities/' + encodeURIComponent(params.identityId),
            data: {
                id: params.identityId,
                label: params.label
            },
            traceId: params.traceId
        });
    };

    // Not used as of December 2022
    _apiService.faces.setIdentityPrimaryImage = function(params) {
        if (!params?.identityId || !params?.imageId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: PUT,
            url: '/faces/identities/' + encodeURIComponent(params.identityId),
            data: {
                id: params.identityId,
                primaryImageId: params.imageId
            },
            traceId: params.traceId
        });
    };

    _apiService.faces.addIdentity = function(params) {
        if (!params?.label) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/faces/identities',
            data: {
                label: params.label
            },
            traceId: params.traceId
        });
    };

    // Not used as of December 2022
    _apiService.faces.removeIdentity = function(params) {
        if (!params?.identityId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: DELETE,
            url: '/faces/identities/' + encodeURIComponent(params.identityId),
            traceId: params.traceId
        });
    };

    // Not used as of December 2022
    _apiService.faces.removeIdentityImage = function(params) {
        if (!params?.imageId || !params?.identityId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: DELETE,
            url: '/faces/identities/' + encodeURIComponent(params.identityId)
                + '/images/' + encodeURIComponent(params.imageId),
            traceId: params.traceId
        });
    };

    // Not used as of December 2022
    _apiService.faces.mergeIdentities = function(params) {
        if (!params?.idKeep || !params?.idRemove || !params?.label) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/faces/identities/do/mergeIdentities',
            data: {
                idKeep: params.idKeep,
                idRemove: params.idRemove,
                name: params.label
            },
            traceId: params.traceId
        });
    };

    // ==============================================================================
    _apiService.schedulers = {};

    _apiService.schedulers.getForDevice = function(params) {
        if (!params?.deviceId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            url: '/schedulers',
            params: {
                deviceId: params.deviceId
            },
            deviceId: params.deviceId,
            traceId: params.traceId
        });
    };

    _apiService.schedulers.remove = function(params) {
        if (!params?.schedulerId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: DELETE,
            url: '/schedulers/' + encodeURIComponent(params.schedulerId),
            id: params.schedulerId,
            traceId: params.traceId
        });
    };

    /**
     * @description This is for modifying existing schedulers only
     * @param {Object} params
     * @param {Object} params.scheduler
     * @param {String} params.scheduler.id Must include id
     * @param {String} params.scheduler.deviceId Must include deviceId
     * @param {String} [params.traceId]
     * @returns {Promise}
     */
    // Not used as of December 2022
    _apiService.schedulers.put = function(params) {
        if (!params?.scheduler?.id || !params?.scheduler?.deviceId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: PUT,
            url: '/schedulers/' + encodeURIComponent(params.scheduler.id),
            data: params.scheduler,
            traceId: params.traceId
        });
    };

    /**
     * @description This is for creating new schedulers only
     * @param {Object} params
     * @param {Object} params.scheduler
     * @param {String} params.scheduler.id
     * @param {String} params.scheduler.deviceId Must include deviceId
     * @param {String} [params.traceId]
     * @returns {Promise}
     */
    // Not used as of December 2022
    _apiService.schedulers.post = function(params) {
        if (!params?.scheduler?.id || !params?.scheduler?.deviceId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/schedulers',
            data: params.scheduler,
            traceId: params.traceId
        });
    };

    // ==============================================================================

    _apiService.vehicles = {};

    _apiService.vehicles.get = function(params) {
        return axiosInstance({
            method: GET,
            url: '/vehicles',
            ignoreAuthModule: true,
            traceId: params?.traceId
        });
    };

    _apiService.vehicles.getById = function(params) {
        if (!params?.vehicleId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            url: '/vehicles/' + encodeURIComponent(params.vehicleId),
            ignoreAuthModule: true,
            traceId: params.traceId
        });
    };

    _apiService.vehicles.put = function(params) {
        if (!params || !params?.vehicle?.id) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: PUT,
            url: '/vehicles/' + encodeURIComponent(params.vehicle.id),
            data: params.vehicle,
            ignoreAuthModule: true,
            traceId: params.traceId
        });
    };

    _apiService.vehicles.post = function(params) {
        if (!params || !params.vehicle) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/vehicles',
            data: params.vehicle,
            ignoreAuthModule: true,
            traceId: params.traceId
        });
    };

    _apiService.vehicles.remove = function(params) {
        if (!params?.vehicleId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: DELETE,
            url: '/vehicles/' + encodeURIComponent(params.vehicleId),
            vehicleId: params.vehicleId,
            ignoreAuthModule: true,
            traceId: params.traceId
        });
    };

    _apiService.vehicles.getImages = function(params) {
        if (!params?.vehicleId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            url: '/vehicles/' + encodeURIComponent(params.vehicleId) + '/images',
            vehicleId: params.vehicleId,
            ignoreAuthModule: true,
            traceId: params.traceId
        });
    };

    _apiService.vehicles.imageUrl = function(params) {
        if (!params?.vehicleId || !params?.imageId) {
            throw badParams(params);
        }
        const url = new URL(environment.apiUrlBase
            + '/vehicles/'
            + encodeURIComponent(params.vehicleId)
            + '/images/'
            + encodeURIComponent(params.imageId)
            + '/image');
        if (environment.oauthToken) {
            url.searchParams.append('accessToken', environment.oauthToken);
        }
        return url.toString();
    };

    _apiService.vehicles.reassign = function(params) {
        if (!params?.vehicleId || !params?.imageId || !params?.newVehicleId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/vehicles/'
                + encodeURIComponent(params.vehicleId)
                + '/images/'
                + encodeURIComponent(params.imageId)
                + '/do/reassignToVehicle',
            data: {
                vehicleId: params.newVehicleId
            },
            ignoreAuthModule: true,
            traceId: params.traceId
        });
    };

    _apiService.vehicles.getMakes = function(params) {
        if (!params?.year) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: GET,
            baseURL: environment.webUrlBase,
            url: '/data/vehicles/years/' + encodeURIComponent(params.year) + '/makes',
            year: params.year,
            ignoreAuthModule: true,
            traceId: params.traceId
        });
    };

    _apiService.vehicles.verifyAll = function(params) {
        return axiosInstance({
            method: POST,
            url: '/vehicles/do/verifyAll',
            ignoreAuthModule: true,
            traceId: params?.traceId
        });
    };

    _apiService.vehicles.verifyDevice = function(params) {
        if (!params?.deviceId) {
            return rejectWithBadParams(params);
        }
        return axiosInstance({
            method: POST,
            url: '/vehicles/do/verifyDevice/' + encodeURIComponent(params.deviceId),
            deviceId: params.deviceId,
            ignoreAuthModule: true,
            traceId: params.traceId
        });
    };

    return Object.freeze(_apiService);
}

export default createApiService;
